import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, Outlet, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Routes, Route, BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { WeatherProvider } from './context/WeatherContext';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import SplashScreen, { STARTUP_SPLASH_DURATION_MS } from './components/SplashScreen';
import { initializeAccessibility } from './utils/accessibility';
import { getPageTitle } from './utils/pageTitle';
import { ToastProvider } from './components/ui/Toast';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import MainLayout from './components/MainLayout';
import AdminLayout from './components/AdminLayout';
import AuthLayout from './components/AuthLayout';
import LandingLayout from './components/LandingLayout';
import AccountClosedModal from './components/AccountClosedModal';
import InstallAppPrompt from './components/InstallAppPrompt';
import AndroidSelectPicker from './components/ui/AndroidSelectPicker';
import NativePushManager from './components/NativePushManager';
import api from './axios_helper';
import './styles/index.css';
import './styles/pages/public-profile.css';

// Keep the first bundle focused on the shell/auth state. Heavy pages are
// fetched only when their route is opened, which prevents the map, wizard,
// forum and admin code from delaying the first feed image.
const Home = lazy(() => import('./pages/Home'));
const Travels = lazy(() => import('./pages/Travels'));
const TravelDetails = lazy(() => import('./components/TravelDetails.jsx'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const HelpSupport = lazy(() => import('./pages/HelpSupport'));
const NotFound = lazy(() => import('./pages/NotFound'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Notifications = lazy(() => import('./components/Notifications'));
const InteractiveMap = lazy(() => import('./pages/InteractiveMap'));
const QandA = lazy(() => import('./pages/QandA'));
const AdminLogin = lazy(() => import('./components/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const Achievements = lazy(() => import('./pages/Achievements'));
const Weather = lazy(() => import('./pages/weather'));
const SettingsAndPrivacy = lazy(() => import('./pages/SettingsAndPrivacy'));
const Feedback = lazy(() => import('./pages/Feedback'));
const FutureTravelsComingSoon = lazy(() => import('./pages/FutureTravelsComingSoon'));
const TripWizard = lazy(() => import('./pages/TripWizard'));
const Landing = lazy(() => import('./pages/Landing'));
const AndroidOnboardingTour = lazy(() => import('./components/AndroidOnboardingTour'));
const PublicPrivacyPolicy = lazy(() => import('./pages/PublicPrivacyPolicy'));
const PublicAccountDeletion = lazy(() => import('./pages/PublicAccountDeletion'));

const isStandalonePwa = () => {
  if (typeof window === 'undefined') return false;

  try {
    return Boolean(
      window.matchMedia?.('(display-mode: standalone)').matches
      || window.matchMedia?.('(display-mode: fullscreen)').matches
      || window.navigator.standalone === true
    );
  } catch (_) {
    return window.navigator?.standalone === true;
  }
};

// Set only by the Android bundle build. The regular site and PWA continue to
// use their existing standalone/browser detection without native-only rules.
const IS_ANDROID_APP_BUILD = process.env.REACT_APP_ANDROID_APP === 'true';
const isInstalledAppSurface = () => IS_ANDROID_APP_BUILD || isStandalonePwa();

// Android's system Back button should follow the React Router history first,
// then return to the authenticated home/login root instead of exiting from an
// arbitrary screen. The module is loaded only in Android builds.
const AndroidBackButtonHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (!IS_ANDROID_APP_BUILD) return undefined;

    let disposed = false;
    let listener;
    import('@capacitor/app')
      .then(({ App: NativeApp }) => NativeApp.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          navigate(-1);
          return;
        }

        const homePath = user ? '/home' : '/login';
        if (location.pathname !== homePath) {
          navigate(homePath, { replace: true });
          return;
        }

        NativeApp.exitApp();
      }))
      .then((subscription) => {
        if (disposed) subscription.remove();
        else listener = subscription;
      })
      .catch((error) => console.warn('[Globe Memories] Android back handler unavailable', error));

    return () => {
      disposed = true;
      listener?.remove();
    };
  }, [navigate, location.pathname, user]);

  return null;
};

// Componente de Rota Protegida
const ProtectedRoute = () => {
  const { user, isLoadingAuth } = useAuth();

  // Enquanto carrega, mostra splash screen
  if (isLoadingAuth) {
    return <SplashScreen />;
  }

  return user ? <Outlet /> : <Navigate to="/login" replace />;
};

// Login and register are public entry points, but they should never be
// shown to someone who already has a valid persisted session. Keeping this
// guard at the route level also covers direct URL access and PWA launches.
const PublicOnlyRoute = () => {
  const { user, isLoadingAuth } = useAuth();

  if (isLoadingAuth) {
    return <SplashScreen />;
  }

  return user ? <Navigate to="/home" replace /> : <Outlet />;
};

// Trip details are an authenticated surface. Discovery cards and public
// profiles may remain reachable before login, but opening a concrete trip
// (including an explicit share link) always goes through authentication.
// Preserve the complete URL so Login/Register can return the visitor to the
// same trip after a successful session.
const PublicTripDetails = () => {
  const { user, isLoadingAuth } = useAuth();
  const location = useLocation();

  if (isLoadingAuth) {
    return <SplashScreen />;
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      />
    );
  }

  return (
    <MainLayout>
      <TravelDetails />
    </MainLayout>
  );
};

// Shared profile links use a clean, Instagram-like standalone surface for
// anonymous visitors. Authenticated users keep the regular application
// shell/sidebar; anonymous visitors see the same full-page surface whether
// the profile is public or private (the profile itself still enforces what is
// visible).
const PublicProfileShell = ({ children }) => (
  <div className="gm-public-profile-shell">
    <header className="gm-public-profile-shell__header" role="banner">
      <Link to="/" className="gm-public-profile-shell__brand" aria-label="Globe Memories — início">
        <img src="/images/Globe-Memories.png" alt="Globe Memories" />
      </Link>
      <nav className="gm-public-profile-shell__actions" aria-label="Acesso">
        <Link to="/login" className="gm-public-profile-shell__login">Entrar</Link>
        <Link to="/register" className="gm-public-profile-shell__register">Criar conta</Link>
      </nav>
    </header>
    <main className="gm-public-profile-shell__main" role="main">
      {children}
    </main>
  </div>
);

const PublicProfileRoute = () => {
  const { user } = useAuth();
  const { username } = useParams();
  const [profileVisibility, setProfileVisibility] = useState(user ? 'authenticated' : 'loading');

  useEffect(() => {
    if (user || !username) {
      setProfileVisibility(user ? 'authenticated' : 'unknown');
      return undefined;
    }
    let cancelled = false;
    const resolveVisibility = async () => {
      try {
        const searchResponse = await api.get('/users/search', {
          params: { query: username, page: 0, size: 20 },
        });
        const matches = searchResponse.data?.content || [];
        const match = matches.find((candidate) => candidate?.username === username);
        if (!match?.id) {
          if (!cancelled) setProfileVisibility('unknown');
          return;
        }
        const detailedResponse = await api.get(`/users/${match.id}/detailed`);
        if (!cancelled) {
          setProfileVisibility(detailedResponse.data?.privateProfile === true ? 'private' : 'public');
        }
      } catch (_) {
        if (!cancelled) setProfileVisibility('unknown');
      }
    };
    resolveVisibility();
    return () => { cancelled = true; };
  }, [user, username]);

  if (!user && profileVisibility === 'loading') {
    return <div className="gm-route-loading" aria-hidden="true" />;
  }

  if (!user && (profileVisibility === 'public' || profileVisibility === 'private')) {
    return (
      <PublicProfileShell>
        <UserProfile />
      </PublicProfileShell>
    );
  }

  // Keep the standalone public shell for every anonymous profile result,
  // including usernames that do not exist. UserProfile renders the proper
  // "Viajante não encontrado" state while the header stays free of the app
  // sidebar and global search, just like the anonymous 404 page.
  if (!user) {
    return (
      <PublicProfileShell>
        <UserProfile />
      </PublicProfileShell>
    );
  }

  return (
    <MainLayout>
      <UserProfile />
    </MainLayout>
  );
};

// 404 pages use the same clean public header as shared profiles when the
// visitor is anonymous. This keeps the error page free of the app sidebar
// and global search while still offering the two authentication actions.
const NotFoundRoute = () => {
  const { user, isLoadingAuth } = useAuth();

  if (isLoadingAuth) return <SplashScreen />;

  return user ? (
    <MainLayout>
      <NotFound />
    </MainLayout>
  ) : (
    <PublicProfileShell>
      <NotFound />
    </PublicProfileShell>
  );
};

/**
 * Marketing pages remain public in a regular browser. An installed PWA is
 * an app surface: anonymous users go to Login and authenticated users go to
 * the feed, including when they enter through `/landing` directly.
 */
const RootRoute = () => {
  const { user, isLoadingAuth } = useAuth();
  if (isLoadingAuth) return <SplashScreen />;
  if (isInstalledAppSurface()) return <Navigate to={user ? '/home' : '/login'} replace />;
  return user ? <Navigate to="/home" replace /> : <Landing />;
};

const LandingRoute = () => {
  const { user, isLoadingAuth } = useAuth();

  if (isInstalledAppSurface()) {
    if (isLoadingAuth) return <SplashScreen />;
    return <Navigate to={user ? '/home' : '/login'} replace />;
  }

  return <Landing />;
};

// CRA serves one HTML shell for every route. Keep the document title useful
// during client-side navigation instead of leaving the homepage title on
// every screen. Public trip titles are managed separately by tripSeo because
// they depend on the trip data returned by the API.
const PageTitleManager = () => {
  const { pathname } = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const title = getPageTitle(pathname);
    if (title) document.title = t(title);
  }, [pathname, t]);

  return null;
};

/**
 * The legacy /my-travels page is gone — trip management (create,
 * edit, delete) now lives in the user's profile (see UserProfile
 * and the Edit/Delete buttons on the travel cards there). Any
 * bookmark or link to the old URL is bounced to the caller's own
 * profile so the UX is coherent with where trips actually live now.
 */
const MyTravelsLegacyRedirect = () => {
  const { user } = useAuth();
  const target = user?.username ? `/profile/${user.username}` : '/login';
  return <Navigate to={target} replace />;
};

const App = () => {
  const { isLoadingAuth } = useAuth();

  // Inicializar melhorias de acessibilidade
  useEffect(() => {
    initializeAccessibility();
  }, []);

  // While we're still loading the persisted auth state, show the
  // splash. The RootRoute will then either render the landing
  // (anonymous) or redirect to /home (signed in).
  if (isLoadingAuth) {
    return <SplashScreen />;
  }

  return (
    <WeatherProvider>
      <Suspense fallback={<div className="gm-route-loading" aria-hidden="true" />}>
        <Routes>
        {/* Auth routes — dedicated AuthLayout (no sidebar, no top bar).
            An already authenticated user goes straight to /home. */}
        <Route element={<PublicOnlyRoute />}>
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
          </Route>
        </Route>

        {/* Password-reset links must remain reachable even if an old session
            is still present in another browser tab. */}
        <Route element={<AuthLayout />}>
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Round 59 — Landing lives in its own bare layout (no
            sidebar, no top bar). The small floating LandingHeader
            already carries the Login/Register CTAs. Mounted BEFORE
            the MainLayout so React Router's path specificity picks
            the right layout for `/` and `/landing`. */}
        <Route element={<LandingLayout />}>
          <Route path="/" element={<RootRoute />} />
          <Route path="/landing" element={<LandingRoute />} />
        </Route>

        {/* Play Store public legal resources; intentionally reachable without
            an account and outside the authenticated application shell. */}
        <Route path="/privacy-policy" element={<PublicPrivacyPolicy />} />
        <Route path="/delete-account" element={<PublicAccountDeletion />} />

        <Route path="/travel/:id" element={<PublicTripDetails />} />

        {/* Keep old preview links working, but send everyone to the real map. */}
        <Route path="/globe-preview" element={<Navigate to="/interactive-map" replace />} />

        {/* Anonymous visitors only bypass the app shell after the profile's
            public/private visibility has been resolved. */}
        <Route path="/profile/:username" element={<PublicProfileRoute />} />

        {/* Rotas da aplicação principal com MainLayout */}
        <Route element={<MainLayout />}>
          <Route path="/home" element={<ProtectedRoute />}>
            <Route index element={<Home />} />
          </Route>
          <Route path="/travels" element={<ProtectedRoute />}>
            <Route index element={<Travels />} />
          </Route>
          {/* The standalone country directory was retired. Keep old links
              useful by sending them back to travel discovery. */}
          <Route path="/countries/*" element={<Navigate to="/travels" replace />} />
          <Route path="/my-travels" element={<ProtectedRoute />}>
            <Route index element={<MyTravelsLegacyRedirect />} />
          </Route>
          <Route path="/my-travels/new" element={<ProtectedRoute />}>
            <Route index element={<MyTravelsLegacyRedirect />} />
          </Route>
          <Route path="/my-travels/:id/edit" element={<ProtectedRoute />}>
            <Route index element={<MyTravelsLegacyRedirect />} />
          </Route>
          {/* Trip creation & edit live on /trip/* (the legacy
              /my-travels/* paths above now redirect to the user's
              profile). */}
          <Route path="/trip/new" element={<ProtectedRoute />}>
            <Route index element={<TripWizard />} />
          </Route>
          <Route path="/trip/:id/edit" element={<ProtectedRoute />}>
            <Route index element={<TripWizard />} />
          </Route>
          {/* /saved-trips used to be a dedicated page; it now lives
              as a sub-tab inside the user's profile (see
              UserProfile → "Viagens Guardadas"). The path is left
              as a 404 in case anyone has a bookmark. */}
          <Route path="/profile/edit/:username" element={<ProtectedRoute />}>
            <Route index element={<EditProfile />} />
          </Route>
          <Route path="/help-support" element={<ProtectedRoute />}>
            <Route index element={<HelpSupport />} />
          </Route>
          <Route path="/users" element={<Navigate to="/travels?tab=travellers" replace />} />
          <Route path="/qanda" element={<ProtectedRoute />}>
            <Route index element={<QandA />} />
          </Route>
          {/* /qanda/:id e /forum/questions/:id — o "Fórum" (QandA)
              mostra perguntas inline na lista, sem detail-page
              dedicada. Para não perder a UX de clicar numa
              notificação "respondeu à tua pergunta" (que antes
              caía em 404 com /forum/questions/:id), abrimos a
              mesma página do Fórum. O user pode ver a pergunta
              no contexto da lista. */}
          <Route path="/qanda/:id" element={<ProtectedRoute />}>
            <Route index element={<QandA />} />
          </Route>
          <Route path="/forum/questions/:id" element={<ProtectedRoute />}>
            <Route index element={<QandA />} />
          </Route>
          <Route path="/forum/comments/:id" element={<ProtectedRoute />}>
            <Route index element={<QandA />} />
          </Route>
          <Route path="/achievements" element={<ProtectedRoute />}>
            <Route index element={<Achievements />} />
          </Route>
          <Route path="/interactive-map" element={<ProtectedRoute />}>
            <Route index element={<InteractiveMap />} />
          </Route>
          <Route path="/plan-travel" element={<ProtectedRoute />}>
            <Route index element={<FutureTravelsComingSoon />} />
          </Route>
          <Route path="/future-travels" element={<ProtectedRoute />}>
            <Route index element={<Navigate to="/trip/new" replace />} />
          </Route>
          <Route path="/Weather" element={<ProtectedRoute />}>
            <Route index element={<Weather />} />
          </Route>
          <Route path="/notifications" element={<ProtectedRoute />}>
            <Route index element={<Notifications />} />
          </Route>
          <Route path="/settings-and-privacy" element={<ProtectedRoute />}>
            <Route index element={<SettingsAndPrivacy />} />
          </Route>
          <Route path="/feedback" element={<ProtectedRoute />}>
            <Route index element={<Feedback />} />
          </Route>
          {/* Redirecionar /blocked-users para /settings-and-privacy#blocked-users */}
          <Route path="/blocked-users" element={<Navigate to="/settings-and-privacy#blocked-users" replace />} />
        </Route>

        {/* The anonymous 404 must not inherit the authenticated app shell:
            it gets the logo + Entrar/Criar conta public header instead. */}
        <Route path="*" element={<NotFoundRoute />} />

        {/* TripWizard (full-screen, sem sidebar/topbar, própria UI)
            está agora registado em /trip/new e /trip/:id/edit dentro
            do MainLayout, com a devida proteção. O legacy
            /my-travels/new e /my-travels/:id/edit ficam como
            redirect para a profile. */}

        {/* Rotas do backoffice com AdminLayout */}
        <Route element={<AdminLayout />}>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route element={<AdminProtectedRoute />}>
            <Route path="/admin/*" element={<AdminDashboard />} />
          </Route>
        </Route>
        </Routes>
      </Suspense>
    </WeatherProvider>
  );
};

const isFreshAppLaunch = () => {
  try {
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (navigation?.type) return navigation.type === 'navigate';

    // Older browsers expose the navigation type through this deprecated API.
    return window.performance?.navigation?.type === 0;
  } catch {
    return false;
  }
};

// In an installed PWA or Android app, play the brand animation on every fresh
// app launch, whether or not the visitor has a session. Keep the existing
// browser behavior for authenticated users and do not replay it for F5 or
// client-side navigation.
// Keep Login unmounted during the animation: iOS Safari can otherwise surface
// a saved-password prompt as soon as it detects the password input. An already
// authenticated route can safely prepare behind the splash instead.
const StartupSplashGate = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const previousUserRef = useRef(user);
  const [visible, setVisible] = useState(() => {
    if (!isFreshAppLaunch()) return false;
    if (isInstalledAppSurface()) return true;
    return Boolean(user) && location.pathname !== '/landing';
  });
  const [minimumElapsed, setMinimumElapsed] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [closing, setClosing] = useState(false);

  useLayoutEffect(() => {
    const justAuthenticated = !previousUserRef.current && Boolean(user);
    previousUserRef.current = user;

    // On the regular website, show the animation only when a successful
    // sign-in/register enters the authenticated app. A PWA launch already
    // showed it before Login mounted, so signing in must not replay it.
    if (justAuthenticated && !isInstalledAppSurface()) setVisible(true);
  }, [user]);

  useEffect(() => {
    if (!visible) return undefined;

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    setMinimumElapsed(false);
    setContentReady(false);
    setClosing(false);
    // The original curtain occupied the last 640 ms of the 3760 ms launch.
    // Preserve that timing, but only start it once the destination is ready.
    const timeoutId = window.setTimeout(
      () => setMinimumElapsed(true),
      prefersReducedMotion ? 140 : STARTUP_SPLASH_DURATION_MS - (user ? 640 : 0),
    );
    // A broken/very slow media request must not trap someone forever on the
    // launch screen. Normal launches finish as soon as the first frame paints.
    const maxWaitId = window.setTimeout(() => setContentReady(true), 12000);
    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(maxWaitId);
    };
  }, [visible, user]);

  useEffect(() => {
    if (!visible) return undefined;

    if (!user) {
      // Download the Login route while the animation runs, without mounting
      // its password field (which would trigger the iOS autofill sheet).
      let cancelled = false;
      import('./pages/Login').then(
        () => { if (!cancelled) setContentReady(true); },
        () => { if (!cancelled) setContentReady(true); },
      );
      return () => { cancelled = true; };
    }

    const checkReady = () => {
      // /login redirects signed-in visitors to /home. Do not release the
      // splash in the intermediate route or over its lazy-loading fallback.
      if (location.pathname === '/login' || document.querySelector('.gm-route-loading')) return;

      const mediaReady = (media) => {
        if (!media) return true;
        const image = media.querySelector('.gm-progimg.is-loaded img');
        const poster = media.querySelector('.gm-unified-video__poster');
        const video = media.querySelector('video');
        return (image?.complete && image.naturalWidth > 0)
          || (poster?.complete && poster.naturalWidth > 0)
          || video?.readyState >= 2
          || Boolean(media.querySelector('.gm-progimg--error'));
      };

      if (location.pathname !== '/home') {
        const details = document.querySelector('.gm-td__container');
        if (details) {
          if (mediaReady(details.querySelector('.gm-td__hero .gm-carousel'))) setContentReady(true);
        } else if (document.querySelector('.gm-app') && !document.querySelector('.gm-route-loading')) {
          setContentReady(true);
        }
        return;
      }

      const feed = document.querySelector('.gm-home__feed');
      if (!feed) return;
      if (feed.querySelector('.gm-home__empty')) {
        setContentReady(true);
        return;
      }

      const firstPost = feed.querySelector('.gm-post');
      if (!firstPost) return;
      const media = firstPost.querySelector('.gm-post__media-frame');
      // ProgressiveImg adds is-loaded only after image.decode() resolves.
      // For a video, its poster or first decoded frame serves the same role.
      if (mediaReady(media)) setContentReady(true);
    };

    const observer = new MutationObserver(checkReady);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    document.addEventListener('load', checkReady, true);
    document.addEventListener('loadeddata', checkReady, true);
    checkReady();
    return () => {
      observer.disconnect();
      document.removeEventListener('load', checkReady, true);
      document.removeEventListener('loadeddata', checkReady, true);
    };
  }, [visible, user, location.pathname]);

  useEffect(() => {
    if (!visible || !minimumElapsed || !contentReady) return undefined;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    if (!user || prefersReducedMotion) {
      setVisible(false);
      return undefined;
    }
    setClosing(true);
    const timeoutId = window.setTimeout(() => setVisible(false), 640);
    return () => window.clearTimeout(timeoutId);
  }, [visible, minimumElapsed, contentReady, user]);

  useEffect(() => {
    if (location.pathname === '/landing' && !isInstalledAppSurface()) setVisible(false);
  }, [location.pathname]);

  return visible ? (
    <>
      {user && children}
      <SplashScreen isLaunch closing={closing} />
    </>
  ) : (
    <>
      {children}
      {IS_ANDROID_APP_BUILD && (
        <Suspense fallback={null}>
          <AndroidOnboardingTour />
        </Suspense>
      )}
    </>
  );
};

const AppWrapper = () => {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <LanguageProvider>
            <StartupSplashGate>
              <ToastProvider>
                <ScrollToTop />
                <AndroidBackButtonHandler />
                <NativePushManager />
                <PageTitleManager />
                <App />
                <AndroidSelectPicker />
                <InstallAppPrompt />
                <AccountClosedModal />
              </ToastProvider>
            </StartupSplashGate>
          </LanguageProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default AppWrapper;
